import React, { useState } from 'react';
import { X, Trophy, User, Mail, Phone, Clock, FileText, CheckCircle, AlertCircle, Upload, File, Shield, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';

// Simple TABLES constant
const TABLES = {
  contest_submissions: 'contest_submissions'
};
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';
import toast from 'react-hot-toast';
// Simple file validation
const SecureFileUpload = {
  validateFile: (file, folder) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg', 'image/gif', 'application/zip'];
    
    if (file.size > maxSize) {
      throw new Error('File size must be less than 10MB');
    }
    
    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type not allowed. Please use PDF, DOC, PNG, JPG, GIF, or ZIP files.');
    }
    
    return {
      securePath: `${folder}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
    };
  }
};
import duplicateSubmissionHandler from '../utils/duplicateSubmissionHandler';

const ContestSubmissionForm = ({ isOpen, onClose, contestData }) => {
  const { user, isVerified } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  useModalScrollLock(isOpen);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    experience: '',
    title: '',
    description: '',
    impact: '',
    consent: false,
    attachmentFile: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';

    if (!formData.experience.trim()) newErrors.experience = 'Experience is required';
    if (!formData.title.trim()) newErrors.title = 'Technique title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.impact.trim()) newErrors.impact = 'Impact/Benefits is required';
    if (!formData.consent) newErrors.consent = 'You must agree to the terms';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, bypassDuplicateCheck = false) => {
    e.preventDefault();
    
    // Check authentication first
    if (!isVerified) {
      setShowAuthModal(true);
      toast.error('Please sign in to submit your contest entry');
      return;
    }
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    // Check for duplicate submissions (unless bypassed)
    if (!bypassDuplicateCheck) {
      try {
        const submissionData = {
          email: formData.email,
          title: formData.title,
          description: formData.description,
          contest_title: contestData?.theme || 'Advanced Testing Methodologies',
          technique_title: formData.title,
          technique_description: formData.description
        };
        
        const duplicateResult = await duplicateSubmissionHandler.checkDuplicate(submissionData);
        
        if (duplicateResult.isDuplicate) {
          // Allow bypass only for similar content, not exact duplicates or limits
          if (duplicateResult.type === 'exact' || duplicateResult.type === 'limit' || duplicateResult.type === 'rate_limit') {
            setDuplicateCheck(duplicateResult);
            setShowDuplicateWarning(true);
            setIsSubmitting(false);
            return;
          } else if (duplicateResult.type === 'similar') {
            setDuplicateCheck(duplicateResult);
            setShowDuplicateWarning(true);
            setIsSubmitting(false);
            return;
          }
        }
      } catch (error) {
        console.warn('Duplicate check failed:', error);
        // Continue with submission if duplicate check fails
      }
    }
    
    try {


      let fileUrl = null;
      
      // Upload file if provided
      if (formData.attachmentFile && supabase) {
        try {
          // Validate and secure the file upload
          const fileValidation = SecureFileUpload.validateFile(
            formData.attachmentFile, 
            'contest-attachments'
          );
          
          const { error: uploadError } = await supabase.storage
            .from('testingvala-bucket')
            .upload(fileValidation.securePath, formData.attachmentFile);
          
          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage
              .from('testingvala-bucket')
              .getPublicUrl(fileValidation.securePath);
            fileUrl = publicUrl;
          }
        } catch (uploadError) {
          console.warn('File upload failed:', uploadError.message);
          toast.error(`File upload failed: ${uploadError.message}`);
        }
      }

      // Demo mode fallback
      if (!supabase) {
        const submissionData = {
          name: formData.fullName,
          email: formData.email,
          mobile: formData.mobile,
          company: 'Not provided',
          role: 'QA Professional',
          experience_years: formData.experience,
          contest_title: contestData?.theme || 'Advanced Testing Methodologies',
          technique_title: formData.title,
          technique_description: formData.description,
          impact_benefits: formData.impact,
          submission_text: `Title: ${formData.title}\n\nDescription: ${formData.description}\n\nImpact/Benefits: ${formData.impact}`,
          submission_file_url: fileUrl,
          status: 'pending',
          created_at: new Date().toISOString(),
          winner_rank: null,
          winner_announcement_timestamp: null
        };
        
        const localSubmissions = JSON.parse(localStorage.getItem('contest_submissions') || '[]');
        const demoSubmission = {
          id: Date.now(),
          ...submissionData,
          created_at: new Date().toISOString(),
          winner_rank: null,
          winner_announcement_timestamp: null
        };
        localSubmissions.push(demoSubmission);
        localStorage.setItem('contest_submissions', JSON.stringify(localSubmissions));
        
        
        // Record successful submission
        duplicateSubmissionHandler.recordSubmission(formData.email);
        
        toast.success('🎉 Contest submission successful! (Demo mode)');
        
        setFormData({
          fullName: '',
          email: '',
          mobile: '',
          experience: '',
          title: '',
          description: '',
          impact: '',
          consent: false,
          attachmentFile: null
        });
        
        onClose();
        return;
      }

      const submissionData = {
        name: formData.fullName,
        email: formData.email,
        mobile: formData.mobile,
        company: 'Not provided',
        role: 'QA Professional',
        experience_years: formData.experience,
        contest_title: contestData?.theme || 'Advanced Testing Methodologies',
        technique_title: formData.title,
        technique_description: formData.description,
        impact_benefits: formData.impact,
        submission_text: `Title: ${formData.title}\n\nDescription: ${formData.description}\n\nImpact/Benefits: ${formData.impact}`,
        submission_file_url: fileUrl,
        status: 'pending',
        created_at: new Date().toISOString(),
        winner_rank: null,
        winner_announcement_timestamp: null
      };

      const { data, error } = await supabase
        .from('contest_submissions')
        .insert([submissionData])
        .select();

      // Also save to localStorage as backup
      const localSubmissions = JSON.parse(localStorage.getItem('contest_submissions') || '[]');
      localSubmissions.push({
        id: Date.now(),
        ...submissionData,
        created_at: new Date().toISOString(),
        winner_rank: null,
        winner_announcement_timestamp: null
      });
      localStorage.setItem('contest_submissions', JSON.stringify(localSubmissions));

      if (error) {
        console.error('Contest submission error:', error);
        // Save to localStorage as fallback
        const localSubmissions = JSON.parse(localStorage.getItem('contest_submissions') || '[]');
        const fallbackSubmission = {
          id: Date.now(),
          ...submissionData,
          created_at: new Date().toISOString(),
          winner_rank: null,
          winner_announcement_timestamp: null
        };
        localSubmissions.push(fallbackSubmission);
        localStorage.setItem('contest_submissions', JSON.stringify(localSubmissions));
        
        // Record successful submission
        duplicateSubmissionHandler.recordSubmission(formData.email);
        
        toast.success('🎉 Contest submission received! We\'ll review your entry soon.');
        
        setFormData({
          fullName: '',
          email: '',
          mobile: '',
          experience: '',
          title: '',
          description: '',
          impact: '',
          consent: false,
          attachmentFile: null
        });
        
        onClose();
        return;
      }

      // Record successful submission
      duplicateSubmissionHandler.recordSubmission(formData.email);
      
      toast.success('🎉 Contest submission successful! We\'ll review your entry and get back to you soon.');
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        mobile: '',
        experience: '',
        title: '',
        description: '',
        impact: '',
        consent: false,
        attachmentFile: null
      });
      
      onClose();
    } catch (error) {
      
      toast.error('Failed to submit entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto">
      <div className="bg-white border-t sm:border border-slate-200 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[94vh] sm:max-h-[90vh] flex flex-col overflow-hidden text-slate-800 my-0 sm:my-auto transition-all">
        {/* Modal Header */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-gradient-to-r from-[#0057B7] via-[#0066CC] to-[#FF6600] flex items-center justify-between gap-3 shadow-md flex-shrink-0">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center shadow-inner flex-shrink-0">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-xl font-bold text-white tracking-tight truncate">Join QA Contest</h2>
                <span className="text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full bg-white/20 text-white border border-white/30 hidden sm:inline-block">
                  Live Contest
                </span>
              </div>
              <p className="text-blue-100 text-xs truncate mt-0.5">{contestData?.theme || 'Advanced Testing Methodologies'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200 flex items-center justify-center min-w-[44px] min-h-[44px]"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} encType="multipart/form-data" className="p-4 sm:p-6 space-y-5 sm:space-y-6 overflow-y-auto custom-scrollbar flex-1 bg-white pb-safe">
          {/* Banner */}
          <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/50 to-orange-50/80 rounded-xl p-4 border border-blue-100 relative overflow-hidden">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-[#FF6600]" />
                  Share Your QA Hacks & Win Rewards!
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Showcase your testing methodologies, automation hacks, or process improvements. Top entries win cash prizes & get featured on testingvala.com!
                </p>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#0057B7] bg-blue-100/60 px-3 py-1 rounded-full border border-blue-200">
              <Clock className="w-3.5 h-3.5" />
              <span>Deadline: {(() => {
                const now = new Date();
                const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                let targetDate = lastDayOfMonth;

                if (contestData?.deadline) {
                  const dateStr = String(contestData.deadline).split('T')[0];
                  const parts = dateStr.split('-');
                  if (parts.length === 3) {
                    const parsedDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
                    if (!isNaN(parsedDate.getTime())) {
                      targetDate = parsedDate;
                    }
                  }
                }
                return targetDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
              })()}</span>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-slate-100">
              <User className="w-4 h-4 text-[#0057B7]" />
              Personal Details
            </h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none transition-all ${
                      errors.fullName ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                    }`}
                    placeholder="e.g. Rahul Sharma"
                  />
                </div>
                {errors.fullName && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.fullName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none transition-all ${
                    errors.email ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                  }`}
                  placeholder="e.g. rahuls@example.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none transition-all ${
                    errors.mobile ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                  }`}
                  placeholder="e.g. +91 9876543210"
                />
                {errors.mobile && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.mobile}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Testing Experience <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.experience}
                  onChange={(e) => handleInputChange('experience', e.target.value)}
                  className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none transition-all ${
                    errors.experience ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                  }`}
                >
                  <option value="">Select Experience</option>
                  <option value="0-1">0-1 years</option>
                  <option value="2-3">2-3 years</option>
                  <option value="4-5">4-5 years</option>
                  <option value="6-8">6-8 years</option>
                  <option value="9-12">9-12 years</option>
                  <option value="13+">13+ years</option>
                </select>
                {errors.experience && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.experience}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Submission Details */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-slate-100">
              <FileText className="w-4 h-4 text-[#FF6600]" />
              Technique & Innovation
            </h4>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Technique / Hack Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none transition-all ${
                  errors.title ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                }`}
                placeholder="e.g. Parallel Execution Pipeline using Playwright & Docker"
              />
              {errors.title && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.title}
                </p>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-xs font-semibold text-slate-700">
                  Detailed Description <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-slate-400">{formData.description.length}/1000</span>
              </div>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none resize-none transition-all ${
                  errors.description ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                }`}
                placeholder="Provide step-by-step details of your testing technique, tools used, and implementation methodology..."
              />
              {errors.description && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.description}
                </p>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="block text-xs font-semibold text-slate-700">
                  Impact & Measurable Benefits <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-slate-400">{formData.impact.length}/500</span>
              </div>
              <textarea
                value={formData.impact}
                onChange={(e) => handleInputChange('impact', e.target.value)}
                rows={3}
                className={`w-full px-3.5 py-2.5 bg-slate-50/80 border rounded-xl focus:bg-white focus:ring-2 focus:ring-[#0057B7]/20 focus:border-[#0057B7] text-sm text-slate-900 placeholder-slate-400 outline-none resize-none transition-all ${
                  errors.impact ? 'border-red-400 bg-red-50/50' : 'border-slate-200'
                }`}
                placeholder="List clear metrics e.g. Reduced execution time by 65%, automated 400+ regression cases, 0 production leaks..."
              />
              {errors.impact && (
                <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.impact}
                </p>
              )}
            </div>

            {/* Optional File Upload */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Supporting Document / Screenshot (Optional)
              </label>
              <div className="border-2 border-dashed border-slate-200 hover:border-[#0057B7] bg-slate-50/50 hover:bg-blue-50/30 rounded-xl p-5 text-center transition-all duration-200">
                {formData.attachmentFile ? (
                  <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <File className="w-7 h-7 text-[#0057B7] flex-shrink-0" />
                      <div className="text-left min-w-0">
                        <p className="text-xs font-bold text-slate-900 truncate">{formData.attachmentFile.name}</p>
                        <p className="text-[11px] text-slate-500">{(formData.attachmentFile.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleInputChange('attachmentFile', null)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-[#0057B7] mx-auto mb-2 opacity-80" />
                    <p className="text-xs font-semibold text-slate-800 mb-1">Drag & drop or click to upload</p>
                    <p className="text-[11px] text-slate-500 mb-3">PDF, DOC, PNG, JPG, ZIP up to 10MB</p>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.zip"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          try {
                            SecureFileUpload.validateFile(file, 'contest-attachments');
                            handleInputChange('attachmentFile', file);
                          } catch (error) {
                            toast.error(error.message);
                            e.target.value = '';
                          }
                        }
                      }}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-blue-50 text-[#0057B7] border border-slate-200 hover:border-[#0057B7]/40 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 shadow-xs"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Browse File
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="consent"
                checked={formData.consent}
                onChange={(e) => handleInputChange('consent', e.target.checked)}
                className="mt-0.5 w-4 h-4 text-[#0057B7] bg-white border-slate-300 rounded focus:ring-[#0057B7] accent-[#0057B7] cursor-pointer"
              />
              <div className="flex-1">
                <label htmlFor="consent" className="text-xs text-slate-700 cursor-pointer leading-relaxed">
                  <span className="font-semibold text-slate-900">I agree to Contest Terms & Showcase Rules</span> <span className="text-red-500">*</span>
                  <p className="mt-1 text-slate-500 text-[11px]">
                    I confirm that this is original work. I authorize TestingVala to showcase winning entries on the platform, leaderboard, and social channels.
                  </p>
                </label>
                {errors.consent && (
                  <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.consent}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-1/3 px-4 py-3 bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 rounded-xl text-sm font-semibold transition-all duration-200 order-2 sm:order-1 shadow-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-2/3 bg-gradient-to-r from-[#0057B7] via-[#0066CC] to-[#FF6600] hover:opacity-95 text-white px-5 py-3 rounded-xl text-sm font-bold shadow-md shadow-[#0057B7]/20 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 order-1 sm:order-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Submitting Entry...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Submit Contest Entry</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {/* Authentication Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          toast.success('Authentication successful! You can now submit your contest entry.');
        }}
        action="contest"
      />
      
      {/* Duplicate Warning Modal */}
      {showDuplicateWarning && duplicateCheck && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-white" />
                <h3 className="text-lg font-bold text-white">Duplicate Submission Detected</h3>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-2">
                    {duplicateCheck.message}
                  </p>
                  
                  {duplicateCheck.existingSubmission && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Existing Submission:</p>
                      <p className="text-sm text-gray-600">
                        "{duplicateCheck.existingSubmission.technique_title || duplicateCheck.existingSubmission.title}"
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Submitted: {new Date(duplicateCheck.existingSubmission.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  
                  {duplicateCheck.suggestions?.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-blue-800 mb-1">Suggestions:</p>
                      {duplicateCheck.suggestions.map((suggestion, index) => (
                        <p key={index} className="text-sm text-blue-700">
                          • {suggestion}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowDuplicateWarning(false);
                    setDuplicateCheck(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Modify Submission
                </button>
                
                {duplicateCheck.type === 'similar' && (
                  <button
                    onClick={async () => {
                      setShowDuplicateWarning(false);
                      setDuplicateCheck(null);
                      // Continue with submission despite similarity warning
                      toast.info('Proceeding with submission...');
                      // Re-trigger submission without duplicate check
                      handleSubmit({ preventDefault: () => {} }, true);
                    }}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    Submit Anyway
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContestSubmissionForm;